// CltvEchart.js
import React from 'react';
import ReactECharts from 'echarts-for-react';

import { color } from 'framer-motion';




// Helper to create line chart options
const createLineChartOption = (title, xData, seriesData, yAxisLabel) => ({
    title: {
        text: title,
        left: 'center',
        textStyle: {
            color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#eee' : '#666',
            fontSize: window.innerWidth <= 668 ? 11 : 18,
            //width: window.innerWidth <= 668 ? 250 : 'auto',  // limit width for better control
            overflow: 'truncate', // or use 'break' if wrapping manually
        }
    },
    tooltip: {
        trigger: 'axis'
    },
    legend: {
        top: window.innerWidth <= 668 ? 20 : 30,
        textStyle: {
            color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#eee' : '#666', // Use theme-based legend color
        },
    },
    xAxis: {
        type: 'category',
        data: xData,
        axisLabel: {
            color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#eee' : '#666',
            rotate: 0
        }
    },
    yAxis: {
        type: 'value',
        name: yAxisLabel,
        nameLocation: 'middle',
        nameGap: window.innerWidth <= 568 ? 30 : 40,
        axisLabel: {
            color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#eee' : '#666',
        }
    },
    grid: {
        //top: 80,                       // space for title + legend
        left: window.innerWidth <= 568 ? 50 : 80,      // give room for Y-axis name
        // right: 20,
        //bottom: 40,
    },
    series: seriesData
});

// Helper to create bar chart options
const createBarChartOption = (title, xData, yData, colorList, yAxisLabel, textLabels) => ({
    title: {
        text: title,
        left: 'center',
        textStyle: {
            fontSize: window.innerWidth <= 668 ? 11 : 18,
            color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#eee' : '#666',
        }
    },
    tooltip: {
        trigger: 'axis',
        axisPointer: {
            type: 'shadow'
        }
    },
    xAxis: {
        type: 'category',
        data: xData,
        axisLabel: {
            rotate: 45,
            color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#eee' : '#666',
        }
    },
    yAxis: {
        type: 'value',
        name: yAxisLabel,
        nameLocation: 'middle',
        nameGap: window.innerWidth <= 568 ? 30 : 50,
        axisLabel: {
            color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#eee' : '#666',
        }
    },
    grid: {
        //top: 80,                       // space for title + legend
        left: window.innerWidth <= 568 ? 50 : 80,      // give room for Y-axis name
        // right: 20,
        //bottom: 40,
    },
    series: [{
        type: 'bar',
        data: yData.map((y, i) => ({ value: y, itemStyle: { color: colorList[i] }, label: { show: true, position: 'top', formatter: textLabels[i] } })),
        label: {
            show: true,
            position: 'top',
            color: window.matchMedia('(prefers-color-scheme: dark)').matches ? '#eee' : '#666',
        }
    }]
});

export const ECltvChartBgnbd = ({ data }) => {
    const option = createLineChartOption(
        'Decile Plot - Actual vs Predicted Frequency',
        data.map(d => d.decile),
        [
            {
                name: 'Average Actual Frequency',
                type: 'line',
                data: data.map(d => d.avg_actual_frequency),
                symbol: 'circle'
            },
            {
                name: 'Average Predicted Frequency',
                type: 'line',
                data: data.map(d => d.avg_predicted_frequency),
                symbol: 'x'
            }
        ],
        'Average Frequency in Holdout Period'
    );
    return <ReactECharts option={option} style={{ height: 500 }} />;
};

export const ECltvChartGgf = ({ data }) => {
    const option = createLineChartOption(
        'Decile Plot - Avg Occasion Value Prediction',
        data.map(d => d.decile),
        [
            {
                name: 'Actual Avg Occasion Value',
                type: 'line',
                data: data.map(d => d.avg_actual),
                symbol: 'circle'
            },
            {
                name: 'Predicted Avg Occasion Value',
                type: 'line',
                data: data.map(d => d.avg_predicted),
                symbol: 'x'
            }
        ],
        'Average Monetary Value per Occasion'
    );
    return <ReactECharts option={option} style={{ height: 500 }} />;
};

export const ECltvChartSpend = ({ data }) => {
    const option = createLineChartOption(
        'Decile Plot - Predicted CLTV vs Actual Holdout Spend',
        data.map(d => d.decile),
        [
            {
                name: 'Avg Actual Holdout Spend',
                type: 'line',
                data: data.map(d => d.avg_actual_spend),
                symbol: 'circle'
            },
            {
                name: 'Avg Predicted CLTV',
                type: 'line',
                data: data.map(d => d.avg_predicted_cltv),
                symbol: 'x'
            }
        ],
        'Average Total Spend in Holdout'
    );
    return <ReactECharts option={option} style={{ height: 500 }} />;
};

export const ECltvChartSegment = ({ data }) => {
    const clvDecileGroups = data.map(d => `Decile ${d.clv_decile_group}`);
    const avgCltv = data.map(d => d.avg_predicted_cltv);
    const customerLabels = data.map(d => `n=${d.number_of_customers}`);

    // gradient: light blue to peach to dark red
    const gradientColors = ['#AEC8FF', '#F2C090', '#BF5500'];
    const interpolateColor = (c1, c2, f) => {
        const hex = h => parseInt(h.replace('#', ''), 16);
        const rgb = hex(c1), rgb2 = hex(c2);
        const r1 = (rgb >> 16) & 255, g1 = (rgb >> 8) & 255, b1 = rgb & 255;
        const r2 = (rgb2 >> 16) & 255, g2 = (rgb2 >> 8) & 255, b2 = rgb2 & 255;
        const r = Math.round(r1 + f * (r2 - r1)), g = Math.round(g1 + f * (g2 - g1)), b = Math.round(b1 + f * (b2 - b1));
        return `rgb(${r},${g},${b})`;
    };
    const colorList = avgCltv.map((_, i) => {
        const f = i / (avgCltv.length - 1);
        return f < 0.5
            ? interpolateColor(gradientColors[0], gradientColors[1], f / 0.5)
            : interpolateColor(gradientColors[1], gradientColors[2], (f - 0.5) / 0.5);
    });

    const option = createBarChartOption(
        'Average Predicted CLTV per Decile',
        clvDecileGroups,
        avgCltv,
        colorList,
        'Average Predicted CLTV',
        customerLabels
    );
    return <ReactECharts option={option} style={{ height: 600 }} />;
};
